import React from 'react';
import PropTypes from 'prop-types';
import VolumeMeter from './VolumeMeter';

import constants from '../../../../qti/Constants';
import { t } from '../../../../services/i18nService';

import styles from './audioRecordInteraction.scss';
import Prompt from '../Prompt';
import utilService from '../../../../services/UtilService';
import marketplacePropertiesService from '../../../../services/MarketplacePropertiesService';
import audioRecordService from '../../../../services/AudioRecordService';

const EXAM_CONTEXT = constants.componentContexts.EXAM;
const PREVIEW_CONTEXT = constants.componentContexts.PREVIEW;

let mediaRecorder;
let recordedFileFormat = null;
const recordedChunks = {};

const STATE_RECORDING_STARTED = 'recording_started';
const STATE_RECORDING_UPLOADING = 'recording_uploading';
const STATE_RECORDING_SAVED = 'recording_saved';

class AudioRecordInteraction extends React.Component {
    static propTypes = {
        callback: PropTypes.func.isRequired,
        context: PropTypes.string.isRequired, // The context in which the question is displayed. Eg: 'exam' or 'preview'.
        handleInteractionReady: PropTypes.func.isRequired,
        isCurrent: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
        model: PropTypes.object.isRequired, // model taken from responseModels related to this interaction
        questionModel: PropTypes.object.isRequired, // model of the question/assessmentItem holding this interaction.
        direction: PropTypes.string,
        services: PropTypes.object.isRequired,
        userResponse: PropTypes.array.isRequired,
    };

    state = {
        recordingState: undefined,
        stream: undefined,
        recordTime: '',
    };

    componentDidMount() {
        setTimeout(() => {
            this.props.handleInteractionReady(true);
        }, 50);
    }

    componentWillUnmount() {
        if (marketplacePropertiesService.isRecordingDurationCounterEnabled()) {
            this.stopTimer();
        }
    }

    startTimer() {
        const startTime = Date.now();
        this.timerIntervalId = setInterval(() => {
            const ms = Date.now() - startTime;
            this.setState({ recordTime: utilService.getReadableDuration(ms, true) });
        }, 1000);
    }

    stopTimer() {
        clearInterval(this.timerIntervalId);
        this.setState({ recordTime: '' });
    }

    createAndStartRecorder(stream, options) {
        const {
            context,
            questionModel: { contentRevisionId },
        } = this.props;

        recordedChunks[contentRevisionId] = [];

        mediaRecorder = new MediaRecorder(stream, options);
        mediaRecorder.addEventListener('dataavailable', ({ data }) => {
            if (data.size > 0) {
                recordedChunks[contentRevisionId].push(data);
                recordedFileFormat = data.type;
            }
        });

        mediaRecorder.addEventListener('stop', () => {
            console.log(recordedChunks[contentRevisionId]);
            if (context === EXAM_CONTEXT) {
                this.setState({
                    recordingState: STATE_RECORDING_UPLOADING,
                });
                this.uploadRecording();
            } else {
                this.setState({
                    recordingState: STATE_RECORDING_SAVED,
                });
            }
        });

        mediaRecorder.start();
    }

    startRecord = () => {
        if (!navigator.mediaDevices && !navigator.mediaDevices.getUserMedia) {
            console.error('Audio recording is not possible: mediaDevices are not found.');
            return;
        }

        if (!MediaRecorder.isTypeSupported) {
            console.error('Audio recording is not possible: MediaRecorder is not supported.');
            return;
        }

        this.setState({
            recordingState: STATE_RECORDING_STARTED,
            stream: undefined,
        });

        const config = {
            audio: true,
            video: false,
        };

        navigator.mediaDevices.getUserMedia(config)
            .then((stream) => {
                if (marketplacePropertiesService.isRecordingDurationCounterEnabled()) {
                    this.startTimer();
                }

                const options = {
                    mimeType: audioRecordService.getSuitableMimeType(),
                };

                this.setState({
                    stream,
                });

                this.createAndStartRecorder(stream, options);
            })
            .catch((error) => {
                console.error('Error when recording', error);
            });
    };

    stopRecord = () => {
        if (mediaRecorder) {
            mediaRecorder.stop();
            // Otherwise Safari will produce a recording with ever-growing echo.
            mediaRecorder.stream.getTracks()[0].stop();
        }
    };

    handleFileUploaded = (file, payload) => {
        const { identifier } = this.props.model;
        this.props.callback(identifier, [payload.contentItemId]);
        this.setState({
            recordingState: STATE_RECORDING_SAVED,
        });
    };

    handleCallback = (type, file, payload, id) => {
        console.log('Callback', {
            type,
            file,
            payload,
            id,
        });

        if (type === 'progress') {
            // this.handleProgress(payload);
        } else if (type === 'complete') {
            // this.setState({ uploadedFileS3Name: payload });
        } else if (type === 'sent-upload-success') {
            this.handleFileUploaded(file, payload);
        } else if (type === 'cancelled') {
            // this.handleCancelled(payload);
        } else if (type === 'error') {
            // this.handleError(payload);
        }
    };

    getEffectiveRecordedAudioType = () => {
        return recordedFileFormat;
    };

    uploadRecording = () => {
        const { questionModel: { contentRevisionId } } = this.props;
        if (recordedChunks[contentRevisionId] && recordedChunks[contentRevisionId].length > 0) {
            const blob = new Blob(recordedChunks[contentRevisionId], {
                type: this.getEffectiveRecordedAudioType(),
            });
            blob.name = 'recording';
            this.uploadController = this.props.services.uploadService.upload(
                blob,
                this.props.questionModel.contentItemId,
                this.props.questionModel.contentRevisionId,
                (type, file, payload, id) => {
                    this.handleCallback(type, file, payload, id);
                }
            );
        }
    };

    getAudioUrl = () => {
        const { userResponse, questionModel: { contentRevisionId } } = this.props;
        // TODO: Being able to listen to student audio when he was using Chrome/FF, but I am using Safari.
        if (userResponse && userResponse.length === 1) {
            return `/file/ci/${userResponse[0]}/recording`;
        } else if (recordedChunks[contentRevisionId] && recordedChunks[contentRevisionId].length > 0) {
            const blob = new Blob(recordedChunks[contentRevisionId], {
                type: this.getEffectiveRecordedAudioType(),
            });
            return URL.createObjectURL(blob);
        }
        return undefined;
    };

    handleButtonClick = () => {
        const { recordingState } = this.state;
        const showStartRecordButton = this.shouldShowStartRecordButton();

        if (showStartRecordButton) {
            this.startRecord();
        } else if (recordingState === STATE_RECORDING_STARTED) {
            this.stopRecord();
            if (marketplacePropertiesService.isRecordingDurationCounterEnabled()) {
                this.stopTimer();
            }
        }
    };

    shouldShowStartRecordButton = () => {
        const { recordingState } = this.state;
        const { context } = this.props;
        return (context === EXAM_CONTEXT || context === PREVIEW_CONTEXT) &&
            !(recordingState === STATE_RECORDING_UPLOADING) &&
            (!recordingState || recordingState !== STATE_RECORDING_STARTED);
    };

    render() {
        const { recordingState, stream } = this.state;
        const isRecordingStarted = recordingState === STATE_RECORDING_STARTED;
        const { model, direction } = this.props;
        const showUploadInProgress = recordingState === STATE_RECORDING_UPLOADING;
        const showVolumeMeter = stream && isRecordingStarted;
        const showStartRecordButton = this.shouldShowStartRecordButton();

        const audioURL = !showVolumeMeter && !showUploadInProgress ? this.getAudioUrl() : undefined;

        let buttonStyle;
        if (showStartRecordButton) {
            buttonStyle = styles.recordButton;
        } else if (isRecordingStarted) {
            buttonStyle = styles.stopButton;
        }

        return (
            <div className="interaction" dir={direction}>
                <Prompt model={model.prompt} />

                { showVolumeMeter && <VolumeMeter stream={stream} /> }

                {/* eslint-disable-next-line jsx-a11y/media-has-caption */ }
                { audioURL && <audio src={audioURL} controls /> }

                { showUploadInProgress && <div className="fa fa-spinner fa-pulse fa-3x fa-fw" /> }
                { (showStartRecordButton || isRecordingStarted) &&
                    <div className={styles.recordButtonsWrapper}>
                        <button className={buttonStyle} onClick={this.handleButtonClick}>
                            {
                                showStartRecordButton &&
                                    <React.Fragment>
                                        <span className="sr-only">{t('interactions.audioRecord.record')}</span>
                                        <i className="fa fa-microphone" aria-hidden="true" />
                                    </React.Fragment>
                            }
                            {
                                isRecordingStarted &&
                                    <React.Fragment>
                                        <span className="sr-only">{t('interactions.audioRecord.stop')}</span>
                                        <i className="fa fa-stop-circle-o" aria-hidden="true" />

                                    </React.Fragment>
                            }
                        </button>

                        { isRecordingStarted && this.state.recordTime?.length > 0 &&
                            <span className={styles.recordTime}>
                                { `${this.state.recordTime} ${t('interactions.audioRecord.recordTime')}` }
                            </span>
                        }
                    </div>
                }
            </div>
        );
    }
}

export default AudioRecordInteraction;
